import { getPortalBaseUrl } from '../js/constants/constants';
import { PERMISSIONS } from '../js/constants/permission-constants';
import { Tag } from '../library/types/Tags';
import { Folder, FolderPermission, FolderPermissions, FolderUpdatePayload, PlatformOrganisationFolder } from '../types/Folder';
import { ListCollection, PagingRequest } from '../types/List';
import { getIsGuest } from '../utilities/user.utilities';
import { getEtag, setEtag } from './etag.service';
import { fetchData } from './fetch-service';
import { createJob } from './jobs.service';
import { getAccessToken, getShareAccessToken } from './token.service';

export interface FolderDownloadPreflight {
    hasRegularFiles: boolean;
    hasModels: boolean;
    regularFileCount: number;
    modelCount: number;
    regularFilesSizeBytes: number;
    modelInputsSizeBytes: number;
    skippedSubFolderCount: number;
}

const PortalBaseUrl = getPortalBaseUrl();

export const getGuestFolders = async (sharedFolderKey: string): Promise<ListCollection<Folder>> => {
    const accessToken = getShareAccessToken();
    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(`${PortalBaseUrl}/api/folders/${sharedFolderKey}/shared`, {
        headers: { Authorization: `ShareToken ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    return response.json();
};

export const getFolders = async (options: PagingRequest, rootFolder?: string): Promise<ListCollection<Folder>> => {
    if (getIsGuest() && rootFolder) {
        return getGuestFolders(rootFolder);
    }

    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(`${PortalBaseUrl}/api/folders${options ? `?page=${options.page}&perpage=${options.perpage}` : ''}`, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    return response.json();
};

export const getFolder = async (key: string): Promise<Folder> => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const path = `${PortalBaseUrl}/api/folders/${key}`;
    const response = await fetchData(path, { headers: { Authorization: `Bearer ${accessToken}` } });

    if (!response.ok) {
        return Promise.reject(response);
    }

    const folder = (await response.json()) as Folder;

    setEtag(key, response.headers.get('etag') ?? '');

    return folder;
};

export const updateFolder = async (body: FolderUpdatePayload, key: string) => {
    await getFolder(key);
    const etag = getEtag(key);
    const accessToken = await getAccessToken();
    const path = `${PortalBaseUrl}/api/folders/${key}`;
    const response = await fetchData(path, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}`, etag },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    setEtag(key, response.headers.get('etag') ?? '');

    return Promise.resolve(true);
};

export const moveFolder = async (body: FolderUpdatePayload, key: string) => {
    const etag = getEtag(key);

    const accessToken = await getAccessToken();

    const path = `${PortalBaseUrl}/api/folders/${key}`;
    const response = await fetchData(path, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}`, etag },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    setEtag(key, response.headers.get('etag') ?? '');

    return Promise.resolve(true);
};

export const deleteFolder = async (key: string): Promise<void> => {
    const accessToken = await getAccessToken();
    await getFolder(key);
    const etag = getEtag(key);

    const path = `${PortalBaseUrl}/api/folders/${key}`;
    const response = await fetchData(path, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}`, etag }
    });

    if (!response.ok) {
        const resp = await response.json();
        return Promise.reject(resp);
    }
};

export const createFolder = async (body: FolderUpdatePayload): Promise<{ key: string }> => {
    const accessToken = await getAccessToken();

    const path = `${PortalBaseUrl}/api/folders`;
    const response = await fetchData(path, {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    const result = response.json();
    return result;
};

export const folderPermissionsForUser = async (folderKey: string) => {
    const permissions = await getFolderUserPermissions(folderKey);

    return permissions;
};

export const getFolderUserPermissions = async (folderKey: string) => {
    // don't get permissions if the user is guest. Swallow the error as it is not pertinent to the guest user
    if (getIsGuest()) {
        return Promise.reject();
    }

    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(
        `${PortalBaseUrl}/api/folders/${folderKey}/userpermissions`,
        {
            headers: { Authorization: `Bearer ${accessToken}` }
        },
        true
    );

    if (!response.ok) {
        return Promise.reject(response);
    }

    setEtag(folderKey, response.headers.get('etag') ?? '');
    const permissions = await response.json();

    return permissions;
};

export const getFolderPermissions = async (folderKey: string): Promise<FolderPermissions> => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(`${PortalBaseUrl}/api/folders/${folderKey}/permissions?includeInherited=true`, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    // remove the create model share key permission before returning.
    const payload = await response.json();

    const inheritedPermissions = payload.inheritsPermissions ? payload.inheritedPermissions : [];

    const permissions = payload.permissions;

    setEtag(folderKey, response.headers.get('etag') ?? '');
    return {
        ...payload,
        permissions: [...permissions],
        inheritedPermissions: [...inheritedPermissions]
    };
};

export const updateFolderPermissions = async (folderKey: string, permissions: FolderPermissions): Promise<void> => {
    const accessToken = await getAccessToken();
    const etag = getEtag(folderKey);

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(`${PortalBaseUrl}/api/folders/${folderKey}/permissions`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}`, etag },
        body: JSON.stringify(permissions)
    });

    if (!response.ok) {
        console.log(await response.json());
        return Promise.reject(response);
    }

    setEtag(folderKey, response.headers.get('etag') ?? '');
};

export const getFolderTags = async (folderKey: string): Promise<Tag[]> => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(`${PortalBaseUrl}/api/folders/${folderKey}/tags`, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    return response.json();
};

export const getPlatformOrganisationFolders = async (orgKey: string): Promise<PlatformOrganisationFolder[]> => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(`${PortalBaseUrl}/api/templates/organisations/${orgKey}/folders`, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    const data: { publicKey: string; name: string; isRootFolder: boolean }[] = await response.json();

    return data
        .map((f) => ({ key: f.publicKey, name: f.name, depth: f.isRootFolder ? 0 : 1 }))
        .sort((a, b) => a.depth - b.depth || a.name.localeCompare(b.name));
};

export const getFolderDownloadPreflight = async (folderKey: string): Promise<FolderDownloadPreflight> => {
    const accessToken = await getAccessToken();

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(`${PortalBaseUrl}/api/folders/${folderKey}/download-preflight`, {
        headers: { Authorization: `Bearer ${accessToken}` }
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    return response.json();
};

export const downloadFolderAsZip = async (folderKey: string, folderName: string, includeModels: boolean): Promise<string> => {
    return createJob({
        jobType: 'DownloadFolder',
        jobTitle: `Download folder ${folderName}`,
        sourceFiles: [],
        typeSpecificAttributes: {
            targetFolderKey: folderKey,
            includeModels
        }
    });
};

export const updateFolderTags = async (key: string, tags: Tag[]): Promise<void> => {
    const accessToken = await getAccessToken();
    const etag = getEtag(key);

    if (!accessToken) {
        return Promise.reject(new Error('Unable to retrieve access token'));
    }

    const response = await fetchData(`${PortalBaseUrl}/api/folders/${key}/tags`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${accessToken}`, etag },
        body: JSON.stringify(tags)
    });

    if (!response.ok) {
        return Promise.reject(response);
    }

    setEtag(key, response.headers.get('etag') ?? '');
};

